<template>
  <div class="vehicle-gallery md:sticky md:top-4">
    <!-- Main Carousel -->
    <div class="relative mb-4 bg-accent/10 rounded-lg overflow-hidden">
      <div class="carousel-container">
        <div class="carousel">
          <div 
            class="carousel-viewport"
            :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
          >
            <div 
              v-for="(photo, index) in photos" 
              :key="index"
              class="carousel-item"
            >
              <img 
                :src="photo"
                :alt="`${title} - Image ${index + 1}`"
                class="carousel-image"
                loading="lazy"
              />
            </div>
          </div>
        </div>
        
              <!-- Thumbnail Counter -->
      <div class="thumbnail-counter absolute bottom-2 right-2 bg-background/80 text-foreground text-xs px-2 py-1 rounded-full z-2">
        {{ currentIndex + 1 }} / {{ photos.length }}
      </div>
      
        <!-- Navigation Buttons -->
        <button 
          v-if="photos.length > 1"
          @click="prevSlide"
          class="absolute sm:flex hidden left-2 top-1/2 -translate-y-1/2 bg-background/80 text-foreground p-2 rounded-full hover:bg-background/90 transition-colors"
          aria-label="Previous image"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        
        <button 
          v-if="photos.length > 1"
          @click="nextSlide"
          class="absolute  sm:flex hidden right-2 top-1/2 -translate-y-1/2 bg-background/80 text-foreground p-2 rounded-full hover:bg-background/90 transition-colors"
          aria-label="Next image"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
        </button>
      </div>
    </div>
    
    <!-- Thumbnails -->
    <template v-if="photos.length > 1">
      <!-- Mobile Scrollable Thumbnails -->
      <div v-if="isMobile" class="thumbnails-container mt-2 relative overflow-hidden">
        <div ref="thumbnailsWrapper" class="thumbnails-wrapper overflow-x-auto pb-2">
          <div class="thumbnails-track flex gap-2">
            <div 
              v-for="(photo, index) in photos" 
              :key="index"
              class="thumbnail-item shrink-0 aspect-square rounded-md overflow-hidden cursor-pointer border-2 transition-colors"
              :class="currentIndex === index ? 'border-primary' : 'border-transparent'"
              @click="currentIndex = index"
              :ref="(el: any) => { if (currentIndex === index && el) activeThumbnail = el }"
            >
              <img 
                :src="photo"
                :alt="`${title} - Thumbnail ${index + 1}`"
                class="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
        
        <!-- Navigation Buttons for Thumbnails -->
        <button 
          v-if="photos.length > 5"
          @click="scrollThumbnails('left')"
          class="absolute left-0 top-1/2 -translate-y-1/2 bg-background/80 text-foreground p-1 rounded-full hover:bg-background/90 transition-colors z-10"
          aria-label="Previous thumbnails"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        
        <button 
          v-if="photos.length > 5"
          @click="scrollThumbnails('right')"
          class="absolute right-0 top-1/2 -translate-y-1/2 bg-background/80 text-foreground p-1 rounded-full hover:bg-background/90 transition-colors z-10"
          aria-label="Next thumbnails"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
        </button>
      </div>
      
      <!-- Desktop Flex-Wrap Thumbnails -->
      <div v-else class="mt-4">
        <div class="flex flex-wrap gap-2 justify-center">
          <button
            v-for="(photo, index) in photos"
            :key="index"
            @click="currentIndex = index"
            class="w-16 h-16 rounded-md overflow-hidden cursor-pointer border-2 transition-colors"
            :class="currentIndex === index ? 'border-primary' : 'border-transparent'"
            :aria-label="`View image ${index + 1}`"
          >
            <img
              :src="photo"
              :alt="`${title} - Thumbnail ${index + 1}`"
              class="w-full h-full object-cover"
              loading="lazy"
            />
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useBreakpoints } from '@vueuse/core'

const props = defineProps<{
  photos: string[];
  title: string;
  initialIndex?: number;
}>()

const currentIndex = ref(props.initialIndex || 0)
const touchStartX = ref(0)
const touchEndX = ref(0)
const thumbnailsWrapper = ref<HTMLElement | null>(null)
const activeThumbnail = ref<HTMLElement | null>(null)

// Setup breakpoints for responsive design
const breakpoints = useBreakpoints({
  mobile: 640,
  tablet: 768,
  desktop: 1024,
})

const isMobile = breakpoints.smaller('tablet')

// Watch for changes in initialIndex
watch(() => props.initialIndex, (newIndex) => {
  if (newIndex !== undefined) {
    currentIndex.value = newIndex
    if (isMobile.value) {
      scrollToActiveThumbnail()
    }
  }
})

// Watch for changes in currentIndex to scroll to active thumbnail on mobile
watch(() => currentIndex.value, () => {
  if (isMobile.value) {
    scrollToActiveThumbnail()
  }
})

// Navigation methods
const nextSlide = () => {
  currentIndex.value = (currentIndex.value + 1) % props.photos.length
}

const prevSlide = () => {
  currentIndex.value = (currentIndex.value - 1 + props.photos.length) % props.photos.length
}

// Thumbnail scrolling for mobile
const scrollThumbnails = (direction: 'left' | 'right') => {
  if (!isMobile.value || !thumbnailsWrapper.value) return
  
  const scrollAmount = 100 // Adjust scroll amount as needed
  if (direction === 'left') {
    thumbnailsWrapper.value.scrollLeft -= scrollAmount
  } else {
    thumbnailsWrapper.value.scrollLeft += scrollAmount
  }
}

// Scroll to active thumbnail for mobile
const scrollToActiveThumbnail = () => {
  if (!isMobile.value) return
  
  setTimeout(() => {
    if (!thumbnailsWrapper.value || !activeThumbnail.value) return
    
    // Get the position of the active thumbnail relative to the wrapper
    const wrapperRect = thumbnailsWrapper.value.getBoundingClientRect()
    const thumbRect = activeThumbnail.value.getBoundingClientRect()
    
    // Calculate the scroll position to center the active thumbnail
    const scrollLeft = (thumbRect.left + thumbRect.width / 2) - (wrapperRect.left + wrapperRect.width / 2)
    
    // Scroll to the active thumbnail
    thumbnailsWrapper.value.scrollLeft += scrollLeft
  }, 50) // Small delay to ensure DOM is updated
}

// Touch event handlers for swipe
const handleTouchStart = (e: Event) => {
  const touchEvent = e as unknown as { changedTouches: { screenX: number }[] }
  if (touchEvent.changedTouches && touchEvent.changedTouches[0]) {
    touchStartX.value = touchEvent.changedTouches[0].screenX
  }
}

const handleTouchEnd = (e: Event) => {
  const touchEvent = e as unknown as { changedTouches: { screenX: number }[] }
  if (touchEvent.changedTouches && touchEvent.changedTouches[0]) {
    touchEndX.value = touchEvent.changedTouches[0].screenX
    handleSwipe()
  }
}

const handleSwipe = () => {
  const minSwipeDistance = 50 // Minimum distance required for a swipe
  const swipeDistance = touchEndX.value - touchStartX.value
  
  if (swipeDistance > minSwipeDistance) {
    // Swiped right, go to previous slide
    prevSlide()
  } else if (swipeDistance < -minSwipeDistance) {
    // Swiped left, go to next slide
    nextSlide()
  }
}

// Add keyboard navigation and touch events
onMounted(() => {
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      prevSlide()
    } else if (e.key === 'ArrowRight') {
      nextSlide()
    }
  }
  
  window.addEventListener('keydown', handleKeyDown)
  
  // Get carousel element for touch events
  const carousel = document.querySelector('.carousel')
  if (carousel) {
    carousel.addEventListener('touchstart', handleTouchStart as EventListener, { passive: true })
    carousel.addEventListener('touchend', handleTouchEnd as EventListener, { passive: true })
  }
  
  onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeyDown)
    
    if (carousel) {
      carousel.removeEventListener('touchstart', handleTouchStart as EventListener)
      carousel.removeEventListener('touchend', handleTouchEnd as EventListener)
    }
  })
})
</script>

<style scoped>
.carousel-container {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.carousel {
  width: 100%;
  overflow: hidden;
}

.carousel-viewport {
  display: flex;
  transition: transform 0.3s ease;
  width: 100%;
}

.carousel-item {
  flex: 0 0 100%;
  width: 100%;
  position: relative;
  aspect-ratio: 16/9;
}

.carousel-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Thumbnails styling */
.thumbnails-container {
  position: relative;
  padding: 0 20px; /* Space for navigation buttons */
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

/* Mobile responsive styles */
@media (max-width: 640px) {
  .carousel-item {
    aspect-ratio: 4/3; /* More suitable aspect ratio for mobile */
  }
  
  .thumbnail-item {
    width: 70px !important; /* Larger thumbnails on mobile for better touch targets */
    min-width: 70px !important; /* Ensure minimum width */
  }
  
  /* Enhance active thumbnail visibility on mobile */
  .thumbnail-item.border-primary {
    border-width: 3px !important; /* Thicker border for active thumbnail */
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1) !important; /* Add subtle shadow */
    transform: scale(1.05) !important; /* Slightly larger */
  }
  
  /* Adjust navigation buttons for mobile */
  .carousel-container button {
    padding: 0.75rem !important; /* Larger padding for better touch targets */
    transform: translateY(-50%); /* Keep original size for better visibility */
    background-color: rgba(255, 255, 255, 0.9) !important; /* More visible background */
  }
  
  /* Adjust thumbnail navigation buttons for mobile */
  .thumbnails-container button {
    padding: 0.5rem !important; /* Larger padding */
    transform: translateY(-50%); /* Keep original size */
    background-color: rgba(255, 255, 255, 0.9) !important; /* More visible background */
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important; /* Add shadow for better visibility */
  }
  
  /* Improve spacing for thumbnails on mobile */
  .thumbnails-track {
    gap: 10px !important; /* More space between thumbnails */
    padding: 8px 0 !important; /* More vertical padding */
  }
  
  /* Ensure thumbnails container has enough space */
  .thumbnails-container {
    padding: 0 30px !important; /* More space for navigation buttons */
    margin-top: 12px !important; /* More space between main carousel and thumbnails */
    max-width: 100% !important; /* Ensure container doesn't overflow */
  }
  
  /* Make thumbnail counter more visible on mobile */
  .thumbnail-counter {
    font-size: 0.75rem !important;
    padding: 0.25rem 0.5rem !important;
    background-color: rgba(255, 255, 255, 0.9) !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
  }
  
  /* Limit the number of visible thumbnails on mobile */
  .thumbnails-wrapper {
    max-width: calc(100vw - 120px) !important; /* Account for padding and buttons */
    margin: 0 auto !important;
  }
}

/* Tablet responsive styles */
@media (min-width: 641px) and (max-width: 1024px) {
  .thumbnails-wrapper {
    max-width: calc(100vw - 80px) !important;
    margin: 0 auto !important;
  }
  
  .thumbnail-item {
    width: 75px !important;
    min-width: 75px !important;
  }
}

.thumbnails-wrapper {
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(155, 155, 155, 0.5) transparent;
  scroll-behavior: smooth;
  width: 100%;
  display: flex;
  justify-content: flex-start;
}

.thumbnails-wrapper::-webkit-scrollbar {
  height: 6px;
}

.thumbnails-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.thumbnails-wrapper::-webkit-scrollbar-thumb {
  background-color: rgba(155, 155, 155, 0.5);
  border-radius: 20px;
}

.thumbnails-track {
  display: flex;
  gap: 8px;
  padding: 4px 0;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
  width: 100%;
  min-width: min-content; /* Ensure it's at least as wide as its content */
}

.thumbnail-item {
  flex: 0 0 auto;
  width: 80px; /* Default width for desktop */
  min-width: 80px; /* Ensure minimum width */
  transition: all 0.2s ease;
}

.thumbnail-item:hover {
  opacity: 0.9;
  transform: scale(1.05);
}
</style>
